import React from 'react';
import { INPUT_STUDENT } from '../../config/designTokens';
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text displayed above the input */
  label?: string;
  /** Error message displayed below the input */
  error?: string;
  /** Helper text displayed below the input (hidden when error is shown) */
  helperText?: string;
  /** Visual variant: admin (compact) or student (larger, more playful) */
  variant?: 'admin' | 'student';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Optional icon element rendered inside the input (left side) */
  icon?: React.ReactNode;
  /** Full width */
  fullWidth?: boolean;
}
export function Input({
  label,
  error,
  helperText,
  variant = 'admin',
  size = 'md',
  icon,
  fullWidth = true,
  className = '',
  id,
  disabled,
  ...props
}: InputProps) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const baseStyles = 'transition-all outline-none bg-white';
  const variants = {
    admin: `border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-400 ${error ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`,
    student: `${INPUT_STUDENT.base} ${error ? INPUT_STUDENT.error : `${INPUT_STUDENT.focus}`}`
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'p-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };
  const studentSizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg'
  };
  const sizeClass = variant === 'student' ? studentSizes[size] : sizes[size];
  const labelStyles = {
    admin: 'block text-sm font-medium text-gray-700 mb-1',
    student: 'block text-sm font-bold text-slate-700 mb-2'
  };
  return <div className={fullWidth ? 'w-full' : ''} data-id="element-2508">
      {label && <label htmlFor={inputId} className={labelStyles[variant]} data-id="element-2509">
          {label}
        </label>}
      <div className="relative" data-id="element-2510">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-2511">
            {icon}
          </span>}
        <input id={inputId} disabled={disabled} className={`
            ${baseStyles}
            ${variants[variant]}
            ${sizeClass}
            ${icon ? 'pl-10' : ''}
            ${fullWidth ? 'w-full' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
            ${className}
          `} aria-invalid={error ? 'true' : undefined} aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined} {...props} data-id="element-2512" />
      </div>
      {error && <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert" data-id="element-2513">
          {error}
        </p>}
      {helperText && !error && <p id={`${inputId}-helper`} className="mt-1 text-xs text-gray-500" data-id="element-2514">
          {helperText}
        </p>}
    </div>;
}